/* @flow */
import * as React from "react";
import * as ReactDOM from "react-dom";
import Measure from "../common/Measure";
import { get } from "../common/fetch";
import SectionData, {
  fetchSection as defaultFetchSection
} from "./SectionData";
import { AnimatedScales, NotAnimatedScales } from "./Scales";
import * as Types from "./NextGenChartTypes";
import * as Helpers from "./NextGenChartMath";
import * as Handlers from "./ChartEventHandlers";
import MobileVisuals from "./NextGenMobileVisuals";
import DesktopVisuals from "./NextGenDesktopVisuals";
import { getScaleModifier, availableOscillators } from "./OscillatorVisuals";

function makeHandler<Props, State, Event>(
  component: React.Component<Props, State>,
  updateState: (Event, ((State, Props) => $Shape<State> | null) => void) => void
) {
  return function(event: Event) {
    updateState(event, fn => component.setState(fn));
  };
}

class Chart extends React.Component<Types.Props, Types.State> {
  state = Handlers.getInitialState(this.props);

  eventHandlers =
    this.props.interactive === false
      ? {}
      : {
          onDragOver: makeHandler(this, Handlers.onDrop),
          onDrop: makeHandler(this, Handlers.onDrop),
          onWheel: makeHandler(this, Handlers.onWheel),
          onMouseDown: makeHandler(this, Handlers.onMouseDown),
          onMouseUp: makeHandler(this, Handlers.onMouseUp),
          onMouseMove: makeHandler(this, Handlers.onMouseMove),
          onMouseEnter: makeHandler(this, Handlers.onMouseEnter),
          onMouseLeave: makeHandler(this, Handlers.onMouseLeave),
          onTouchStart: makeHandler(this, Handlers.onTouchStart),
          onTouchMove: makeHandler(this, Handlers.onTouchMove),
          onTouchEnd: makeHandler(this, Handlers.onTouchEnd)
        };

  containerNode = null;

  attachEventListeners = () => {
    let node = this.containerNode;
    let handlers = this.eventHandlers;

    // Третий аргумент — `capture`. Нужен чтобы отобрать
    // событие из лап нативного зума в сафари.
    node.addEventListener("dragover", handlers.onDragOver, false);
    node.addEventListener("drop", handlers.onDrop, false);
    node.addEventListener("wheel", handlers.onWheel, false);
    node.addEventListener("mousedown", handlers.onMouseDown, false);
    node.addEventListener("mouseup", handlers.onMouseUp, false);
    node.addEventListener("mousemove", handlers.onMouseMove, false);
    node.addEventListener("mouseenter", handlers.onMouseEnter, false);
    node.addEventListener("mouseleave", handlers.onMouseLeave, false);
    node.addEventListener("touchstart", handlers.onTouchStart, true);
    node.addEventListener("touchmove", handlers.onTouchMove, true);
    node.addEventListener("touchend", handlers.onTouchEnd, true);
  };

  detachEventListeners = () => {
    let node = this.containerNode;
    let handlers = this.eventHandlers;

    if (node == null) {
      return;
    }

    // Третий аргумент — `capture`. Нужен чтобы отобрать
    // событие из лап нативного зума в сафари.
    node.removeEventListener("dragover", handlers.onDragOver, false);
    node.removeEventListener("drop", handlers.onDrop, false);
    node.removeEventListener("wheel", handlers.onWheel, false);
    node.removeEventListener("mousedown", handlers.onMouseDown, false);
    node.removeEventListener("mouseup", handlers.onMouseUp, false);
    node.removeEventListener("mousemove", handlers.onMouseMove, false);
    node.removeEventListener("mouseenter", handlers.onMouseEnter, false);
    node.removeEventListener("mouseleave", handlers.onMouseLeave, false);
    node.removeEventListener("touchstart", handlers.onTouchStart, true);
    node.removeEventListener("touchmove", handlers.onTouchMove, true);
    node.removeEventListener("touchend", handlers.onTouchEnd, true);
  };

  handleMeasure = (measures: { width: number, height: number }) => {
    this.setState(measures);
  };

  preserveTimeSpan = () => {
    this.props.dispatch({
      type: "UPDATE_SETTINGS",
      payload: { timeSpan: this.state.timeSpan }
    });
  };

  handleTimeSpanChange = (fn: *) => {
    this.setState(() => ({ isEngaged: true, lastEngagementTime: Date.now() }));
    this.setState(Handlers.resetCursor);
    this.setState(fn);
    this.setState(Handlers.normalizeTimeSpan);
    this.setState(Handlers.saveSnapshot);
  };

  handleBackToReality = () => {
    this.setState((state, props) => {
      const present = props.points[props.points.length - 1];

      return {
        cursor: {
          date: present.date,
          ratio: 0.68,
          animate: true,
          follow: true
        }
      };
    });
    this.setState(Handlers.clearSnapshot);
  };

  handleData = (payload: {
    section: Types.Section,
    data: Array<Types.Point>
  }) => {
    ReactDOM.unstable_batchedUpdates(() => {
      const key = Helpers.getSectionKey(payload.section);
      const [start, end] = Helpers.getSectionBoundaries(payload.section);

      this.setState((state, props) => {
        const relevantCheckpoints = state.checkpoints.filter(
          checkpoint =>
            checkpoint.source[0] <= end && checkpoint.source[1] >= start
        );

        let data = payload.data;

        if (relevantCheckpoints.length > 0 && Array.isArray(data)) {
          data = data.filter(point =>
            relevantCheckpoints.every(
              checkpoint =>
                !(
                  checkpoint.source[0] < point.epoch * 1000 &&
                  checkpoint.source[1] > point.epoch * 1000
                )
            )
          );
        }

        return {
          cache: {
            ...state.cache,
            [key]: data
          }
        };
      });

      this.setState(Handlers.updateIsLoading);
      this.setState(Handlers.updateExtent);
    });
  };

  handleIntervalChange = (interval: number) => {
    ReactDOM.unstable_batchedUpdates(() => {
      this.props.dispatch({
        type: "UPDATE_SETTINGS",
        payload: { interval: interval / 1000 }
      });

      this.setState({ interval: interval / 1000 });
      this.setState({
        animateTimeSpan: false
      });
      this.setState(Handlers.normalizeTimeSpan);
      this.setState(Handlers.saveSnapshot);
    });
  };

  UNSAFE_componentWillReceiveProps(nextProps: Types.Props) {
    if (nextProps.stock !== this.props.stock && nextProps.points.length > 1) {
      this.setState((state, props) =>
        Handlers.updateIsLoading(state, nextProps)
      );
    }

    if (
      nextProps.points.length > 1 &&
      nextProps.points[nextProps.points.length - 1] !==
        this.props.points[this.props.points.length - 1]
    ) {
      this.setState((state, props) => Handlers.updateExtent(state, nextProps));

      const present = nextProps.points[nextProps.points.length - 1];

      this.setState((state, props) => {
        if (state.cursor.follow === true) {
          const interval =
            props.settings.chartType === "line" ? 1 : state.interval;

          if (state.lastMovementTime + interval * 1000 <= present.date) {
            return {
              lastMovementTime: present.date,
              cursor: {
                ...state.cursor,
                animate: true,
                date:
                  state.cursor.date + (present.date - state.lastMovementTime)
              }
            };
          } else {
            return null;
          }
        } else {
          return null;
        }
      });
    }

    if (
      nextProps.settings.chartType !== this.props.settings.chartType &&
      nextProps.points.length > 1
    ) {
      this.setState({
        animateTimeSpan: false
      });
      this.setState((state, props) =>
        Handlers.normalizeTimeSpan(state, nextProps)
      );
    }
  }

  handleMouseUp = () => {
    this.setState({ isActive: false });
  };

  componentDidMount() {
    document.addEventListener("mouseup", this.handleMouseUp);

    // По идее этого тут быть не должно, график должен был инициализироваться
    // с правильными экстентами. Но этого не делал.
    this.setState(Handlers.updateExtent);

    const twoWeekTime = 60 * 60 * 24 * 14;

    const till =
      Math.ceil(
        this.props.points[this.props.points.length - 1].date /
          1000 /
          twoWeekTime
      ) *
        twoWeekTime +
      twoWeekTime;
    const from = till - twoWeekTime * 13; // полгода тому назад

    get(
      `/api/stock-locked-schedule?asset=${
        this.props.stock.stock
      }&from=${from}&till=${till}`
    ).then(payload => {
      const ranges = payload.data.map(weekend => [
        1000 * weekend.from,
        1000 * weekend.till
      ]);

      let checkpoints: Array = [];

      for (let i = 0; i < ranges.length; i++) {
        const range = ranges[i];
        if (checkpoints.length === 0) {
          checkpoints.push({
            type: "negative",
            source: range,
            value: range[0],
            offset: range[0]
          });

          checkpoints.push({
            type: "positive",
            source: range,
            value: range[1],
            offset: range[0]
          });
        } else {
          const lastCheckpoint = checkpoints[checkpoints.length - 1];

          const offset =
            lastCheckpoint.offset + (range[0] - lastCheckpoint.source[1]);

          checkpoints.push({
            type: "negative",
            source: range,
            value: range[0],
            offset: offset
          });

          checkpoints.push({
            type: "positive",
            source: range,
            value: range[1],
            offset: offset
          });
        }
      }

      this.setState({
        checkpoints
      });
    });
  }

  componentWillUnmount() {
    document.removeEventListener("mouseup", this.handleMouseUp);

    if (this.engagementTimer != null) {
      clearTimeout(this.engagementTimer);
    }

    if (this.scrollingTimer != null) {
      clearTimeout(this.scrollingTimer);
    }
  }

  engagementTimer = null;
  scrollingTimer = null;

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.isEngaged === true &&
      this.state.lastEngagementTime !== prevState.lastEngagementTime
    ) {
      if (this.engagementTimer != null) {
        clearTimeout(this.engagementTimer);
      }

      this.engagementTimer = setTimeout(() => {
        this.setState(() => ({ isEngaged: false }));
      }, 5000);
    }

    if (this.state.lastScrollTime !== prevState.lastScrollTime) {
      if (this.scrollingTimer != null) {
        clearTimeout(this.scrollingTimer);
      }

      this.scrollingTimer = setTimeout(() => {
        ReactDOM.unstable_batchedUpdates(() => {
          this.setState(state => ({
            isScrolled: false
          }));
          this.setState(Handlers.normalizeViewport);
          this.setState(Handlers.saveSnapshot);
        });
      }, 100);
    }

    if (
      this.props.settings.interval !== prevProps.settings.interval &&
      this.props.points.length > 1
    ) {
      this.setState((state, props) => ({
        interval: Handlers.getInitialState(props).interval
      }));
      this.setState(Handlers.normalizeTimeSpan);
      this.setState(Handlers.saveSnapshot);
    }
  }

  render() {
    if (this.props.points.length < 2) {
      return null;
    }

    const indicators = this.props.settings.indicators.filter(
      indicator => indicator.enabled !== false
    );

    let oscillators = [];
    if (indicators && indicators.length) {
      oscillators = indicators.filter(
        indicator =>
          indicator.isOscillator === true &&
          availableOscillators.indexOf(indicator.type) > -1
      );
    }

    const scaleModifier = getScaleModifier(this.props.settings.indicators);

    const present = this.props.points[this.props.points.length - 1];

    const payloadProps = Handlers.makePayloadProps(this.state, this.props);

    const relevantSectionProps = Helpers.getCommonSectionProps(payloadProps);

    let relevantSections = Helpers.generateSections(
      relevantSectionProps,
      payloadProps
    );

    const displayedSections =
      this.state.isLoading && this.state.lastLoadedSections != null
        ? Helpers.generateSections(
            {
              ...this.state.lastLoadedSections,
              quantity: Math.ceil(
                this.state.timeSpan / this.state.lastLoadedSections.length
              )
            },
            payloadProps
          )
        : relevantSections;

    let { points, emptySections, isDarkArea, rawPoints } = Helpers.getPoints(
      displayedSections,
      this.state.cache,
      payloadProps,
      this.props.deals
    );

    // Второй круг для тёмных областей графика
    if (isDarkArea) {
      relevantSections = Helpers.generateSections(
        { ...relevantSectionProps, interval: 5000 },
        payloadProps
      );

      const fallback = Helpers.getPoints(
        relevantSections,
        this.state.cache,
        payloadProps,
        this.props.deals
      );

      points = fallback.points;
      emptySections = fallback.emptySections;
      isDarkArea = fallback.isDarkArea;
    }

    const payload = {
      stock: this.props.stock.stock,
      data: points,
      emptySections: emptySections,
      isLoading: this.state.isLoading,
      interval: relevantSectionProps.interval
    };

    let allowSideEffects =
      !Helpers.isShortHistoryStock(this.props.stock) ||
      this.props.allowShortHistorySections;

    const sideEffects =
      !allowSideEffects || this.state.width === 0
        ? null
        : relevantSections
            .filter(section => {
              return (
                Helpers.getSectionType(section, payloadProps) !== "weekend" &&
                Helpers.getSectionBoundaries(section)[0] <=
                  this.props.points[0].date
              );
            })
            .map(section => {
              const key = Helpers.getSectionKey(section);

              return (
                <SectionData
                  key={key}
                  type={Helpers.getSectionType(section, payloadProps)}
                  cache={this.state.cache[key]}
                  section={section}
                  onData={this.handleData}
                  profile={this.props.profile}
                  fetchSection={this.props.fetchSection || defaultFetchSection}
                />
              );
            });

    let Scales = AnimatedScales;

    if (this.props.animated !== true) {
      Scales = NotAnimatedScales;
    } else if (this.state.extent[0] === 0 && this.state.extent[1] === 0) {
      Scales = NotAnimatedScales;
    }

    return (
      <React.Fragment>
        {sideEffects}

        <Scales
          scaleModifier={scaleModifier}
          cursor={this.state.cursor}
          present={present}
          timeSpan={this.state.timeSpan}
          animateTimeSpan={this.state.animateTimeSpan}
          width={this.state.width}
          extent={this.state.extent}
          height={this.state.height}
          onRest={this.preserveTimeSpan}
          checkpoints={this.state.checkpoints}
        >
          {scales => (
            <Measure onMeasure={this.handleMeasure}>
              {measure => {
                let Visuals = this.props.visuals;

                switch (this.props.visuals) {
                  case "mobile":
                    Visuals = MobileVisuals;
                    break;
                  case "desktop":
                    Visuals = DesktopVisuals;
                    break;
                  default:
                    break;
                }

                const points = Helpers.generateRenderablePoints(
                  payload.data,
                  scales.scaleX,
                  scales.scaleY,
                  payload.stock,
                  payload.interval,
                  scales.present,
                  this.props.deals
                );

                const candles =
                  this.props.settings.chartType === "line"
                    ? []
                    : Helpers.generateCandles(
                        payload.data,
                        scales.scaleX,
                        scales.scaleY,
                        payload.stock,
                        payload.interval
                      );

                return (
                  <Visuals
                    time={this.props.time}
                    lastPoint={[
                      scales.scaleX(scales.present.date),
                      scales.scaleY(scales.present.value),
                      scales.present
                    ]}
                    rawData={rawPoints || payload.data}
                    points={points}
                    candles={candles}
                    emptySections={payload.emptySections}
                    sections={relevantSections}
                    realtimeThreshold={this.props.points[0].date}
                    scaleX={scales.scaleX}
                    scaleY={scales.scaleY}
                    loading={this.state.isLoading}
                    width={this.state.width}
                    height={this.state.height}
                    mouseX={this.state.mouseX}
                    mouseY={this.state.mouseY}
                    isHovered={this.state.isHovered}
                    isActive={this.state.isActive}
                    deals={this.props.deals}
                    settings={this.props.settings}
                    intent={this.props.intent}
                    stats={this.props.stats}
                    timeSpan={this.state.timeSpan}
                    onBackToReality={this.handleBackToReality}
                    onTimeSpanChange={this.handleTimeSpanChange}
                    onIntervalChange={this.handleIntervalChange}
                    interval={payload.interval}
                    containerProps={{
                      ref: node => {
                        measure.innerRef(node);

                        if (this.props.interactive !== false) {
                          if (node != null) {
                            this.containerNode = node;
                            this.attachEventListeners();
                          } else {
                            this.detachEventListeners();
                            this.containerNode = node;
                          }
                        }
                      }
                    }}
                    stock={this.props.stock}
                    isEngaged={this.state.isEngaged}
                    oscillators={oscillators}
                    dispatch={this.props.dispatch}
                    cursor={this.state.cursor}
                    checkpoints={this.state.checkpoints}
                    onEditIndicator={this.props.onEditIndicator}
                    tradeMode={this.props.tradeMode}
                    graphicalMode={this.props.graphicalMode}
                    rectTop={this.state.rectTop}
                    outerCountainerRef={this.props.forwardedRef}
                    interactive={this.props.interactive !== false}
                    refundClickHandler={this.props.refundClickHandler}
                    refundFetching={this.props.refundFetching}
                  />
                );
              }}
            </Measure>
          )}
        </Scales>
      </React.Fragment>
    );
  }
}

function ResillientToLackOfCacheChart(props: Types.Props) {
  if (props.points.length < 2) {
    return null;
  } else {
    return <Chart {...props} />;
  }
}

export default ResillientToLackOfCacheChart;



// WEBPACK FOOTER //
// ./src/components/Chart/ClickhouseChart.js